// utils/dates.ts
import {
  parseISO,
  isValid,
  parse,
  differenceInCalendarDays,
  differenceInMonths,
  addMonths,
  isAfter,
} from 'date-fns';

/** Converte vários tipos para Date válida; retorna null se não der. */
export function toDate(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === 'number') {
    // epoch ms
    const d = new Date(value);
    return isValid(d) ? d : null;
  }

  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return null;

    // 1) tenta ISO (2025-09-24T23:00:00Z / 2025-09-24T23:00:00)
    const iso = parseISO(s);
    if (isValid(iso)) return iso;

    // 2) tenta formatos comuns (STRICT)
    const fmts = [
      "yyyy-MM-dd'T'HH:mm:ssXXX", // 2025-09-24T23:00:00+01:00
      "yyyy-MM-dd'T'HH:mm:ss",    // 2025-09-24T23:00:00
      'yyyy-MM-dd',               // 2025-09-24
      'dd/MM/yyyy',               // 24/09/2025
      'dd-MM-yyyy',               // 24-09-2025
      'MM/dd/yyyy',               // 09/24/2025
      'yyyy/MM/dd',               // 2025/09/24
      "dd/MM/yyyy HH:mm:ss",      // 24/09/2025 13:45:00
    ];

    for (const f of fmts) {
      const d = parse(s, f, new Date());
      if (isValid(d)) return d;
    }
  }

  return null;
}

/** Diferença em dias (calendário), clamp >= 0. */
export function diffDays(start: Date, end: Date): number {
  return Math.max(0, differenceInCalendarDays(end, start));
}

/**
 * Formata uma Date para 'yyyy-MM-dd' usando os componentes locais (ano/mês/dia).
 * NÃO usar `date.toISOString()` para isto: aplica UTC e pode recuar um dia
 * consoante o timezone do browser (ex.: p-calendar devolve meia-noite local).
 */
export function toApiDateString(date: Date | null | undefined): string | null {
  if (!date || !isValid(date)) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Diferença exata em meses “calendário” + dias remanescentes. */
export function diffMonthsAndDays(
  start: Date,
  end: Date
): { months: number; days: number } {
  if (!isValid(start) || !isValid(end) || end < start) {
    return { months: 0, days: 0 };
  }

  let months = differenceInMonths(end, start);
  let anchor = addMonths(start, months);

  // Se o “anchor” ultrapassou o fim, recua 1 mês
  if (isAfter(anchor, end)) {
    months -= 1;
    anchor = addMonths(start, months);
  }

  const days = Math.max(0, differenceInCalendarDays(end, anchor));
  return { months, days };
}
